import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../../config/firebase";
import { DataGrid } from "@mui/x-data-grid";
import GridToolbarNoExport from "../../common/GridToolbarNoExport";
import {
  Dialog,
  DialogContent,
  Button,
  Typography,
  Breadcrumbs,
  Box,
  Snackbar,
  Alert,
  Checkbox,
} from "@mui/material";
import CcpCpBreadcrumbs from "../../Layout/inc/CcpCpBreadcrumbs";
import ModalDialogTitle from "../../common/ModalDialogTitle";
import ConfirmDeleteDialog from "../../common/ConfirmDeleteDialog";

const COLLECTION = "CoursePlanningForm";

const CourseEntriesAdmin = () => {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogRow, setDialogRow] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [reloadFlag, setReloadFlag] = useState(false);

  useEffect(() => {
    const fetchRowsAndSchools = async () => {
      // 1. Fetch all course entries
      const snapshot = await getDocs(collection(db, COLLECTION));
      const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

      // 2. Extract unique schoolIds
      const uniqueSchoolIds = [
        ...new Set(data.map((row) => row.schoolId).filter(Boolean)),
      ];

      // 3. Fetch each school by id
      const schoolMap = {};
      await Promise.all(
        uniqueSchoolIds.map(async (schoolId) => {
          const q = query(
            collection(db, "CcpSchools"),
            where("school_id", "==", schoolId),
          );
          const schoolSnap = await getDocs(q);
          if (!schoolSnap.empty) {
            const schoolData = schoolSnap.docs[0].data();
            schoolMap[schoolId] = schoolData.school_name;
          }
        }),
      );

      // 4. Add school_name to each row
      const rowsWithSchoolName = data.map((row) => ({
        ...row,
        schoolName: schoolMap[row.schoolId] || "",
      }));

      setRows(rowsWithSchoolName);
    };
    fetchRowsAndSchools();
  }, [reloadFlag]);

  const handleRowClick = (params) => {
    setDialogRow(params.row);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setDialogRow(null);
  };

  const handleDelete = async () => {
    for (const id of selected) {
      await deleteDoc(doc(db, COLLECTION, String(id)));
    }
    setSnackbar({
      open: true,
      message: "Deleted selected entries.",
      severity: "success",
    });
    setDeleteConfirmOpen(false);
    setSelected([]);
    setReloadFlag((flag) => !flag);
  };

  const handleDeleteClick = () => {
    setDeleteConfirmOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Format date to mm-dd-yyyy, robust to Firestore Timestamp, ISO, and US/UK ambiguity
  function formatDate(dateValue) {
    if (!dateValue) return "";
    let d;
    // Firestore Timestamp
    if (typeof dateValue === "object" && dateValue.seconds) {
      d = new Date(dateValue.seconds * 1000);
    } else if (typeof dateValue === "number") {
      // Milliseconds since epoch
      d = new Date(dateValue);
    } else if (typeof dateValue === "string") {
      // Try ISO first
      d = new Date(dateValue);
      if (isNaN(d)) {
        // Try parsing as MM/DD/YYYY
        const parts = dateValue.split(/[\/-]/);
        if (parts.length === 3) {
          // If year is first, treat as YYYY-MM-DD
          if (parts[0].length === 4) {
            d = new Date(
              Number(parts[0]),
              Number(parts[1]) - 1,
              Number(parts[2]),
            );
          } else {
            // Otherwise, treat as DD-MM-YYYY or MM-DD-YYYY, guess by value
            const [a, b, c] = parts.map(Number);
            if (a > 12) {
              // DD-MM-YYYY
              d = new Date(c, b - 1, a);
            } else if (b > 12) {
              // MM-DD-YYYY (US)
              d = new Date(c, a - 1, b);
            } else {
              // Ambiguous, default to MM-DD-YYYY
              d = new Date(c, a - 1, b);
            }
          }
        }
      }
    } else {
      return String(dateValue);
    }
    if (isNaN(d)) return String(dateValue);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${month}-${day}-${year}`;
  }

  // Dynamically generate columns from first row, excluding unwanted fields
  let columns = rows[0]
    ? Object.keys(rows[0])
        .filter(
          (key) =>
            ![
              "id",
              "hsComments",
              "proctorName",
              "modality",
              "endTime",
              "createdBy",
              "days",
              "startTime",
              "endDate",
              "startDate",
              "hsInstructorName",
              "schoolId",
            ].includes(key),
        )
        .map((key) => ({ field: key, headerName: key, flex: 1 }))
    : [];

  // Add schoolName as the first column
  columns = [
    {
      field: "schoolName",
      headerName: "School",
      flex: 1,
    },
    ...columns,
  ];

  // In the DataGrid column definition, format createdAt
  const createdAtColIndex = columns.findIndex(
    (col) => col.field === "createdAt",
  );
  if (createdAtColIndex !== -1) {
    columns[createdAtColIndex] = {
      ...columns[createdAtColIndex],
      renderCell: (params) => formatDate(params.value),
    };
  }

  return (
    <Box sx={{ p: 3 }}>
      <CcpCpBreadcrumbs
        extra={[{ label: "Course Entries", path: "/admin/course-entries" }]}
      />
      <Typography variant="h4" gutterBottom>
        Course Entries
      </Typography>
      {selected.length > 0 && (
        <Button
          variant="contained"
          color="error"
          sx={{ mb: 2 }}
          onClick={handleDeleteClick}
        >
          Delete Selected
        </Button>
      )}
      <Box sx={{ height: 600, width: "100%", mb: 2 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          checkboxSelection
          disableSelectionOnClick
          getRowId={(row) => String(row.id)}
          onRowClick={handleRowClick}
          rowSelectionModel={selected}
          onRowSelectionModelChange={(ids) => setSelected(ids.map(String))}
          components={{ Toolbar: GridToolbarNoExport }}
        />
      </Box>
      {/* Row Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
      >
        <ModalDialogTitle onClose={handleDialogClose}>
          Course Entry Details
        </ModalDialogTitle>
        <DialogContent dividers>
          {dialogRow && (
            <Box>
              {Object.entries(dialogRow).map(([key, value]) => (
                <Box key={key} sx={{ mb: 1 }}>
                  <Typography variant="subtitle2">
                    {key === "schoolId" || key === "schoolName"
                      ? "School"
                      : key}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {key === "schoolId" || key === "schoolName"
                      ? dialogRow.schoolName || dialogRow.schoolId || ""
                      : key === "createdAt"
                        ? formatDate(value)
                        : String(value)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <ConfirmDeleteDialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDelete}
        title="Delete Confirmation"
        message={`Are you sure you want to delete ${selected.length} selected entr${
          selected.length === 1 ? "y" : "ies"
        }?`}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CourseEntriesAdmin;
